import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../core/data/redux/store";
import { fetchEmployeeOrganizationTree, getEmployeeByUserId } from "../../core/data/redux/employee/employeeAction";
import { Popover } from "antd";
import UnsignedEmployeesModal from "./unsignedEmployeeModal";
import { RightOutlined } from "@ant-design/icons";

const OrgCard = ({ employee, onClick, isSelected = false, employeeHierarchy }: any) => {
  const isTopEmployee = employeeHierarchy && employeeHierarchy[0]?.EmployeeID === employee.EmployeeID;
  const hasSubordinates = employee.Subordinates && employee.Subordinates.length > 0;
  const showArrow = isTopEmployee || hasSubordinates;
  const content = (
    <div
      style={{
        minWidth: "200px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: "8px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        padding: "8px 12px",
      }}
    >
      <div
        style={{
          fontWeight: 600,
          fontSize: "13px",
          color: "#111827",
          marginBottom: "6px",
          borderBottom: "1px solid #f3f4f6",
          paddingBottom: "4px",
        }}
      >
        Employee Details
      </div>

      {[
        ["Name", employee.Name],
        ["Email", employee.Email],
        ["Phone", employee.Phone],
        ["Supervisor", employee.SupervisorName || "N/A"],
        ["Department", employee.Department || "N/A"],
        ["Designation", employee.Designation || "N/A"],
      ].map(([label, value]) => (
        <div
          key={label}
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "12px",
            marginBottom: "4px",
          }}
        >
          <span style={{ color: "#6b7280", fontWeight: 500 }}>{label}</span>
          <span
            style={{
              color: "#111827",
              fontWeight: 500,
              maxWidth: "120px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={value}
          >
            {value}
          </span>
        </div>
      ))}
    </div>
  );

  const nodeContent = (
    <div
      className="org-card"
      style={{
        display: "flex",
        alignItems: "center",
        background: "#ffffff",
        borderRadius: "8px",
        border: isSelected ? "2px solid #4f46e5" : "1px solid #e5e7eb",
        padding: "12px 14px",
        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        boxSizing: "border-box",
        cursor: "pointer",
        transition: "all 0.2s ease",
        gap: "12px",
        width: "240px",
      }}
      onClick={() => onClick(employee)}
    >
      <img
        src={
          employee.PictureURL ||
          "https://cdn-icons-png.flaticon.com/512/149/149071.png"
        }
        alt="profile"
        style={{
          width: "55px",
          height: "55px",
          borderRadius: "50%",
          objectFit: "cover",
          border: "2px solid #f3f4f6",
          flexShrink: 0,
        }}
      />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0,
          flex: 1,
          textAlign: "left",
        }}
      >
        <div
          style={{
            fontSize: "14px",
            fontWeight: 600,
            color: "#111827",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            marginBottom: "4px",
          }}
          title={employee.Name}
        >
          {employee.Name}
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "#6b7280",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={employee.Designation || "No designation"}
        >
          {employee.Designation || "No designation"}
        </div>
      </div>
      {showArrow && (
        <RightOutlined style={{ fontSize: "14px", color: "#6b7280" }} />
      )}
    </div>
  );


  return (
    <Popover content={content} title="" trigger="hover">
      {nodeContent}
    </Popover>
  );
};

const findEmployeeInHierarchy = (employees: any[], id: number): any => {
  for (const employee of employees) {
    if (employee.EmployeeID === id) {
      return employee;
    }

    if (employee.Subordinates && employee.Subordinates.length > 0) {
      const found = findEmployeeInHierarchy(employee.Subordinates, id);
      if (found) return found;
    }
  }
  return null;
};

const buildBreadcrumbPath = (employees: any[], targetId: number, path: any[] = []): any[] => {
  for (const employee of employees) {
    if (employee.EmployeeID === targetId) {
      return [...path, employee];
    }

    if (employee.Subordinates && employee.Subordinates.length > 0) {
      const newPath = buildBreadcrumbPath(employee.Subordinates, targetId, [...path, employee]);
      if (newPath.length > 0) return newPath;
    }
  }
  return [];
};

const OrganizationTree = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: any) => state.auth);
  const { employeeHierarchy } = useSelector((state: any) => state.employee);
  const [currentEmployeeId, setCurrentEmployeeId] = useState<any>("");
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [breadcrumbPath, setBreadcrumbPath] = useState<any[]>([]);

  useEffect(() => {
    if (!employeeHierarchy.length) {
      dispatch(fetchEmployeeOrganizationTree());
    }
    dispatch(getEmployeeByUserId({ userId: user.UserId })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        const empId = res.payload.data.employeeId;
        setCurrentEmployeeId(empId);

        const currentEmp = findEmployeeInHierarchy(employeeHierarchy, empId);
        if (currentEmp) {
          setSelectedEmployee(currentEmp);
          setBreadcrumbPath(buildBreadcrumbPath(employeeHierarchy, empId));
        }
      }
    });
    if (employeeHierarchy.length > 0) {
      const topEmployee = employeeHierarchy[0];
      setSelectedEmployee(topEmployee);
      setBreadcrumbPath(buildBreadcrumbPath(employeeHierarchy, topEmployee.EmployeeID));
    }
  }, [dispatch, employeeHierarchy.length]);


  const handleEmployeeClick = (employee: any) => {
    setSelectedEmployee(employee);
    setBreadcrumbPath(buildBreadcrumbPath(employeeHierarchy, employee.EmployeeID));
  };

  const handleBreadcrumbClick = (employee: any, index: number) => {
    setSelectedEmployee(employee);
    setBreadcrumbPath(breadcrumbPath.slice(0, index + 1));
  };

  const renderSubordinatesGrid = (subordinates: any[]) => {
    const isTopEmployeeSelected = selectedEmployee && selectedEmployee.EmployeeID === employeeHierarchy[0].EmployeeID;

    if ((!subordinates || subordinates.length === 0) && !isTopEmployeeSelected) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "10px",
            color: "#6b7280",
            fontStyle: "italic",
          }}>
          No direct reports
        </div>
      );
    }

    return (
      <div
        className="row justify-content-center"
        style={{
          border: "1px solid #4f46e5",
          borderRadius: "12px",
          padding: "18px",
          marginTop: "20px",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 2fr))",
          gap: "20px",
          justifyContent: "center",
        }}
      >
        {subordinates.map((sub) => (
          <OrgCard
            key={sub.EmployeeID}
            employee={sub}
            onClick={handleEmployeeClick}
            isSelected={selectedEmployee && selectedEmployee.EmployeeID === sub.EmployeeID}
          />
        ))}

        {isTopEmployeeSelected && (<div>{isTopEmployeeSelected && <UnsignedEmployeesModal />}</div>)}
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ padding: "20px" }}>
          {breadcrumbPath.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginBottom: "20px",
                gap: "12px",
              }}
            >
              {breadcrumbPath.map((employee, index) => {
                const isTopEmployee = employeeHierarchy && employeeHierarchy[0]?.EmployeeID === employee.EmployeeID;
                const hasSubordinates = employee.Subordinates && employee.Subordinates.length > 0;
                const showArrow = isTopEmployee || hasSubordinates;

                return (
                  <React.Fragment key={employee.EmployeeID}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        background: "#ffffff",
                        borderRadius: "8px",
                        border: index === breadcrumbPath.length - 1 ? "2px solid #4f46e5" : "1px solid #e5e7eb",
                        padding: "10px 14px",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
                        cursor: "pointer",
                        width: "240px",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => handleBreadcrumbClick(employee, index)}
                    >
                      <div style={{ flexShrink: 0, marginRight: "12px" }}>
                        <img
                          src={employee.PictureURL || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                          alt="profile"
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            border: "2px solid #f3f4f6",
                          }}
                        />
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#111827",
                            marginBottom: "3px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={employee.Name}
                        >
                          {employee.Name}
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#6b7280",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={employee.Designation || "No designation"}
                        >
                          {employee.Designation || "No designation"}
                        </div>
                      </div>
                      {showArrow && (<RightOutlined style={{ fontSize: "14px", color: "#6b7280", marginLeft: "8px" }} />)}
                    </div>

                    {index < breadcrumbPath.length - 1 && (
                      <div
                        style={{
                          width: "2px",
                          height: "16px",
                          background: "#d1d5db",
                          margin: "2px 0",
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          {selectedEmployee ? (
            renderSubordinatesGrid(selectedEmployee.Subordinates || [])
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "40px",
                backgroundColor: "#fefefe",
                borderRadius: "12px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              }}
            >
              <img
                src="https://cdn-icons-png.flaticon.com/512/4076/4076549.png"
                alt="No Data"
                style={{ width: "100px", marginBottom: "20px", opacity: 0.8 }}
              />
              <h2 style={{ color: "#555", marginBottom: "10px" }}>
                No Organizational Structure
              </h2>
              <p style={{ color: "#888", fontSize: "15px", lineHeight: "1.6" }}>
                You currently don't have a valid employee structure, or the data
                is missing manager or supervisor information. Please contact your
                system administrator for assistance.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationTree;
